import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useBusinessContext } from '../contexts/BusinessContext';
import { authService } from '../services/authService';
import { subscriptionService } from '../services/subscriptionService';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading: authLoading, user } = useBusinessContext();
  const [subscriptionChecking, setSubscriptionChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  // Platform roles that bypass subscription checks
  const platformRoles = ['ApplicationAdmin', 'Support'];

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      checkAccess();
    }
  }, [isAuthenticated, authLoading, user]);

  const checkAccess = async () => {
    try {
      setSubscriptionChecking(true);

      // Platform admins bypass subscription checks entirely
      const userRole = user?.role || authService.getUserRole();
      if (platformRoles.includes(userRole)) {
        setHasAccess(true);
        setSubscriptionChecking(false);
        return;
      }

      // First check trial status from JWT
      const trialStatus = authService.getTrialStatus();
      
      // If trial is active, grant access
      if (trialStatus && !trialStatus.isTrialExpired && trialStatus.hasValidAccess) {
        setHasAccess(true);
        setSubscriptionChecking(false);
        return;
      }

      // Trial expired or no trial info - check subscription status
      try {
        const subscriptionStatus = await subscriptionService.getStatus();
        
        // Check if subscription is cancelled
        if (subscriptionStatus && subscriptionStatus.subscriptionStatus === 'Cancelled') {
          setIsCancelled(true);
          setHasAccess(false);
          setSubscriptionChecking(false);
          return;
        }
        
        // Grant access if they have an active or trialing subscription
        if (subscriptionStatus && 
            (subscriptionStatus.subscriptionStatus === 'Active' || 
             subscriptionStatus.subscriptionStatus === 'Trial')) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        // If subscription check fails (404, 401, etc), they don't have access
        console.error('Subscription check failed:', error);
        setHasAccess(false);
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setSubscriptionChecking(false);
    }
  };

  // Show loading state while checking auth or subscription
  if (authLoading || subscriptionChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Skip subscription checks for platform roles
  const userRole = user?.role || authService.getUserRole();
  const isPlatformRole = platformRoles.includes(userRole);

  // Authenticated but subscription is cancelled (not for platform roles)
  if (!isPlatformRole && isCancelled) {
    return <Navigate to="/subscription-cancelled" replace />;
  }

  // Authenticated but no access - trial expired and no subscription (not for platform roles)
  if (!isPlatformRole && !hasAccess) {
    return <Navigate to="/trial-expired" replace />;
  }

  // Check role-based access if allowedRoles specified
  if (allowedRoles.length > 0) {
    if (!allowedRoles.includes(userRole)) {
      // User doesn't have required role - redirect to dashboard or admin
      const redirectTo = isPlatformRole ? '/admin' : '/dashboard';
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Has access - render the protected content
  return children;
};

export default ProtectedRoute;